import React, { useEffect, useState } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import {
  ControllerEliminarDoctor,
  ListarDoctores,
  ValidarCantidadDoctores,
} from "../../Service/Service.doctor";
import DoctorCreate from "./DoctorCreate";
import usePermissions from "../../hook/usePermissions";
import useListaLocal from "../../hook/useListaLocal";
import DataTable from "../UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../UI/DataTable/piezas";
import { ChipEstado } from "../UI/Chip";
import MenuAcciones from "../UI/MenuAcciones";
import Boton from "../UI/Boton";
import Modal from "../UI/Modal/Modal";
import { useToast } from "../UI/Aviso";
import { CampoTexto } from "../UI/Campo";
import { IconoMas, IconoEditar, IconoEliminar, IconoBuscar } from "../UI/Iconos";

export default function Doctor({ tabs }) {
  // RBAC-CUTOVER-07: Nuevo/Editar/Eliminar dependen de doctors.create/
  // doctors.edit/doctors.delete (backend ya los exige en doctors.routes.ts).
  const { hasPermission } = usePermissions();
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const [ping, setPing] = useState(false);
  const [doctores, setDoctores] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [editDoctor, setEditDoctor] = useState(null);
  const [loadingValidacion, setLoadingValidacion] = useState(false);
  const [limite, setLimite] = useState(null);
  const [porBorrar, setPorBorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const { mostrar } = useToast();
  // `doctors_all` devuelve la lista entera y no acepta busqueda ni pagina:
  // se busca y se pagina sobre lo que ya llego.
  const lista = useListaLocal(doctores, {
    campos: ["nombre", "email", "celular", "titulo", "especialidades.especialidad"],
  });

  const EditarDoctor = async (items) => {
    setEditDoctor(items);
    handleShow();
  };

  const handleClose = () => {
    setEditDoctor(null);
    setShow(false);
  };

  const OpenCreateDoctor = async () => {
    setLoadingValidacion(true);
    try {
      const validacion = await ValidarCantidadDoctores();

      if (!validacion.puede_crear) {
        // El límite del plan no es un error: es una condición del contrato, y
        // hay que poder LEERLA con calma (incluye un teléfono al que llamar).
        // Por eso se queda en pantalla hasta cerrarla, no en un aviso que se
        // desvanece.
        setLimite(validacion.message);
        return;
      }

      setEditDoctor(null);
      handleShow();
    } catch (error) {
      mostrar({ tipo: "error", texto: "No se pudo verificar el límite de doctores" });
    } finally {
      setLoadingValidacion(false);
    }
  };

  const eliminarDoctor = async (id) => {
    setBorrando(true);
    const response = await ControllerEliminarDoctor(id);
    setBorrando(false);
    setPorBorrar(null);
    if (response) {
      setPing(!ping);
      mostrar({ tipo: "exito", texto: "El registro ha sido eliminado." });
    } else {
      // Antes, un borrado fallido no decía nada: el `if (response)` no tenía
      // `else` y la fila simplemente seguía ahí.
      mostrar({ tipo: "error", texto: "No se pudo eliminar el doctor." });
    }
  };

  useEffect(() => {
    (async () => {
      setCargando(true);
      const doct = await ListarDoctores();
      if (doct) {
        setDoctores(doct.data);
        setError(null);
      } else {
        setDoctores([]);
        setError("No se pudo obtener el listado de doctores.");
      }
      setCargando(false);
    })();
  }, [ping, tabs.doctores]);

  const columnas = [
    { key: "iddoctor", titulo: "#", ancho: 60, alinear: "derecha", render: (d) => <Celda valor={d.iddoctor} mono /> },
    {
      // Quién es y qué ejerce: título y especialidad son la misma respuesta.
      key: "nombre",
      titulo: "Doctor",
      render: (d) => (
        <CeldaDoble
          principal={d.nombre ? tituloDeNombre(d.nombre) : "Sin determinar"}
          secundario={[d.titulo, d.especialidades?.especialidad ?? "Sin especialidad"].filter(Boolean).join(" · ")}
        />
      ),
    },
    {
      key: "contacto",
      titulo: "Contacto",
      ancho: 240,
      render: (d) => <CeldaDoble principal={d.email} secundario={d.celular} />,
    },
    { key: "genero", titulo: "Género", ancho: 110, render: (d) => <Celda valor={d.genero} /> },
    {
      key: "activo",
      titulo: "Estado",
      ancho: 120,
      render: (d) => (
        <ChipEstado tono={d.activo ? "hecho" : "anulado"}>{d.activo ? "Activo" : "Inactivo"}</ChipEstado>
      ),
    },
    {
      key: "acciones",
      titulo: "",
      ancho: 60,
      alinear: "centro",
      render: (d) => {
        const acciones = [];
        if (hasPermission("doctors.edit")) {
          acciones.push({ label: "Editar", icono: IconoEditar, onClick: () => EditarDoctor(d) });
        }
        if (hasPermission("doctors.delete")) {
          acciones.push({ label: "Eliminar", icono: IconoEliminar, peligro: true, onClick: () => setPorBorrar(d) });
        }
        return acciones.length > 0 ? <MenuAcciones acciones={acciones} titulo={`Acciones de ${d.nombre}`} /> : null;
      },
    },
  ];

  return (
    <section>
      {hasPermission("doctors.create") && (
        <div className="d-flex justify-content-end mb-3">
          <Boton
            variante="primario"
            icono={IconoMas}
            onClick={OpenCreateDoctor}
            cargando={loadingValidacion}
            data-testid="doctor-crear"
          >
            Crear Doctor
          </Boton>
        </div>
      )}

      <div className="mb-3" style={{ maxWidth: 380 }}>
        <CampoTexto
          etiqueta="Buscar doctor"
          icono={IconoBuscar}
          placeholder="Nombre, email, teléfono o especialidad"
          value={lista.texto}
          onChange={(e) => lista.setTexto(e.target.value)}
          data-testid="doctor-buscar"
        />
      </div>

      <DataTable
        columnas={columnas}
        filas={lista.visibles}
        filaKey="iddoctor"
        cargando={cargando}
        error={error}
        onReintentar={() => setPing(!ping)}
        etiquetaTabla="Doctores"
        hayFiltrosAplicados={lista.hayBusqueda}
        onLimpiarFiltros={lista.limpiar}
        textoVacioSinFiltros="Todavía no hay ningún doctor registrado."
        paginacion={lista.paginacion}
      />

      {/* Antes: `¿Estás seguro? No podrás revertir esto` -- una pregunta que no
          decía a quién se estaba borrando. */}
      <Modal
        abierto={Boolean(porBorrar)}
        titulo="Eliminar doctor"
        destructivo
        onCerrar={() => setPorBorrar(null)}
        secundario={{ label: "Cancelar" }}
        primario={{
          label: "Eliminar",
          cargando: borrando,
          onClick: () => eliminarDoctor(porBorrar.iddoctor),
        }}
      >
        <p>
          Vas a eliminar a <b>{porBorrar?.nombre}</b>
          {porBorrar?.especialidades?.especialidad ? ` (${porBorrar.especialidades.especialidad})` : ""}. Esta acción
          no se puede deshacer.
        </p>
      </Modal>

      <Modal
        abierto={Boolean(limite)}
        titulo="Límite de usuarios alcanzado"
        onCerrar={() => setLimite(null)}
        primario={{ label: "Entendido", onClick: () => setLimite(null), icono: false }}
      >
        <p>
          Su compañía ha alcanzado el límite de usuarios de acuerdo a su plan. Contáctese al: <b>0995652112</b>.
        </p>
        {limite && <p className="mb-0">{limite}</p>}
      </Modal>

      <Offcanvas
        style={{ width: "50%" }}
        placement="end"
        show={show}
        onHide={handleClose}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{editDoctor ? "Actualizar" : "Crear"} Doctor</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <DoctorCreate
            handleClose={handleClose}
            setPing={setPing}
            ping={ping}
            editDoctor={editDoctor}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </section>
  );
}
